//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext } from 'react';
import { makeStyles } from '@material-ui/core';
import { useFormik } from 'formik';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { SNACK_BAR_MESSAGE_PASSWORD_SAVED } from '../../common/constants';
import { checkPasswordStrength } from '../../common/authHelper';

import { REQUIRED, PASSWORDS_DO_NOT_MATCH, ERROR_MESSAGE_WEAK_PASSWORD } from '../../common/constants';
import { PLACEHOLDER_FIELD_CONFIRM_PASSWORD, PLACEHOLDER_FIELD_NEW_PASSWORD } from '../../common/placeholderHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdTypography from '../../common/elemental/AfitdTypography';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
import _forEach from 'lodash/forEach';
import _set from 'lodash/set';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: { width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' },
    form: { width: '100%', maxWidth: 500, display: 'flex', flexDirection: 'column', alignItems: 'center' },
    inputBox: {
        flex: 1,
        padding: '0 5px',
        [theme.breakpoints.down('sm')]: { padding: '12px 0px' },
    },
    label: {
        padding: '5px 0',
        fontWeight: 'bold',
    },
    buttonBox: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        padding: '10px 0',
        marginTop: 16,
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column-reverse',
            justifyContent: 'center',
            padding: '0px',
            marginTop: 16,
        },
    },
    heading: {
        fontWeight: 'bold',
    },

    rightMarginToLeftButton: { marginRight: 16 },
    generalAvatarContainer: { display: 'flex', margin: '30px 0', [theme.breakpoints.down('sm')]: { flexDirection: 'column' } },
    hidden: { display: 'none' },
    cropContainer: { height: 250, width: '100%', textAlign: 'center' },
    imgBeforeCropContainer: { display: 'flex', justifyContent: 'center' },
    uploadLinearProgressContainer: { marginTop: 30, padding: '0 20px' },
    generalSaveButton: { [theme.breakpoints.down('sm')]: { width: '100%', marginBottom: 16 } },
    inputBoxes: { width: '100%', marginTop: 30 },
    newPasswordInput: { marginTop: 10, maxWidth: 500 },
    newPassword: { width: '100%' },
    confirmNewPassword: { width: '100%' },
    newPasswordError: {
        width: '100%',
        fontSize: '12px',
        color: 'gray',
        marginLeft: '10px',
    },
    confirmNewPasswordError: {
        width: '100%',
        fontSize: '12px',
        color: 'gray',
        marginLeft: '10px',
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, formik } = params;

    const defns = {
        generalSaveButton: {
            disabled: formik.values.confirmNewPassword === '' && formik.values.newPassword === '',
            color: 'primary',
            type: 'submit',
        },

        heading: { variant: 'h6' },
        label: { variant: 'body2' },
        newPassword: {
            placeholder: PLACEHOLDER_FIELD_NEW_PASSWORD,
            type: 'password',
            id: 'newPassword',
            ...formik.getFieldProps('newPassword'),
        },
        newPasswordError: {
            value: formik.touched.newPassword && formik.errors?.newPassword,
        },

        confirmNewPassword: {
            placeholder: PLACEHOLDER_FIELD_CONFIRM_PASSWORD,
            type: 'password',
            id: 'confirmNewPassword',
            ...formik.getFieldProps('confirmNewPassword'),
        },
        confirmPasswordError: {
            value: formik.touched.confirmNewPassword && formik.errors?.confirmNewPassword,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};
//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const validate = values => {
    const errors = {};
    const fieldNames = ['newPassword', 'confirmNewPassword'];

    // required fields for login and sign up
    _forEach(fieldNames, fieldName => {
        if (!_get(values, fieldName)) {
            _set(errors, fieldName, REQUIRED);
        }
    });

    if (!errors.newPassword) {
        if (!checkPasswordStrength(_get(values, 'newPassword'))) {
            errors.newPassword = ERROR_MESSAGE_WEAK_PASSWORD;
        }
    }
    // password must equal confirm password
    if (!errors.newPassword && !errors.confirmNewPassword) {
        if (values.newPassword !== values.confirmNewPassword) {
            errors.newPassword = errors.confirmNewPassword = PASSWORDS_DO_NOT_MATCH;
        }
    }

    return errors;
};
//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const ResetPasswordForm = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const {
        // actionCode,
        setSnackbarMessage,
        // setSnackbarErrorMessage
    } = useContext(LayoutContext);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { onSubmit } = props;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Formik
    //---------------------------------------------------------------------
    const formik = useFormik({
        initialValues: {
            newPassword: '',
            confirmNewPassword: '',
        },
        validate: validate.bind(null),
        validateOnBlur: false,
        validateOnChange: true,
        onSubmit: async values => handleSubmitForm(values),
    });

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const updatePassword = () => {
        setSnackbarMessage(SNACK_BAR_MESSAGE_PASSWORD_SAVED);
    };

    const handleSubmitForm = async values => {
        const newPassword = _get(values, 'newPassword');
        onSubmit && onSubmit(newPassword);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        updatePassword,
        formik,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <AfitdTypography {...defns.heading}>Reset Password</AfitdTypography>
            <form onSubmit={formik.handleSubmit} {...defns.form}>
                <div {...defns.inputBoxes}>
                    <div {...defns.newPasswordInput}>
                        <AfitdTypography {...defns.label}>New Password</AfitdTypography>
                        <AfitdTextField {...defns.newPassword} />
                        <AfitdTypography {...defns.newPasswordError} />
                    </div>
                    <div {...defns.newPasswordInput}>
                        <AfitdTypography {...defns.label}>New Confirm Password</AfitdTypography>
                        <AfitdTextField {...defns.confirmNewPassword} />
                        <AfitdTypography {...defns.confirmNewPasswordError} />
                    </div>
                </div>

                <div className={classes.buttonBox}>
                    <AfitdButton {...defns.generalSaveButton}>Submit</AfitdButton>
                </div>
            </form>
        </div>
    );
};

export default ResetPasswordForm;
